import type { FC } from "react";
import { ASSISTANT_AVATAR } from "../../../../../theme/tokens";
import { AssistantAvatarEyes } from "../../assistantAvatarConfig";
import { starPath } from "../../assistantAvatarGeometry";
import { AVATAR_PART } from "../../assistantAvatarMotion";

interface EyeProps {
	x: number;
	ink: string;
}

const LEFT_EYE_X = 46;
const RIGHT_EYE_X = 74;
const { FACE } = ASSISTANT_AVATAR;

/** A closed, smiling eye (^). */
const ArcEye: FC<EyeProps> = ({ x, ink }) => <path d={`M${x - 7} 62 Q${x} 53 ${x + 7} 62`} fill="none" stroke={ink} strokeWidth={3.6} strokeLinecap="round" />;

const DotEye: FC<EyeProps> = ({ x, ink }) => (
	<>
		<ellipse cx={x} cy={60} rx={4.8} ry={6.2} fill={ink} />
		<circle cx={x + 1.6} cy={57.6} r={1.6} fill={FACE.GLINT} />
	</>
);

// Round eyes have white sclera, so their pupils keep the dark ink even on a dark body.
const RoundEyes: FC = () => (
	<g className={AVATAR_PART.EYES}>
		<ellipse cx={LEFT_EYE_X} cy={60} rx={8.2} ry={9.6} fill={FACE.SCLERA} />
		<ellipse cx={RIGHT_EYE_X} cy={60} rx={8.2} ry={9.6} fill={FACE.SCLERA} />
		<g className={AVATAR_PART.PUPILS}>
			{[LEFT_EYE_X, RIGHT_EYE_X].map((x) => (
				<g key={x}>
					<circle cx={x} cy={61} r={4.8} fill={ASSISTANT_AVATAR.FEATURE_INK.ON_LIGHT} />
					<circle cx={x + 1.8} cy={59} r={1.6} fill={FACE.SCLERA} />
				</g>
			))}
		</g>
	</g>
);

type EyesRenderer = FC<{ ink: string }>;

// Happy and wink have a closed eye, so they sit outside the blinking group.
const EYES: Record<AssistantAvatarEyes, EyesRenderer> = {
	[AssistantAvatarEyes.Round]: RoundEyes,
	[AssistantAvatarEyes.Dots]: ({ ink }) => (
		<g className={AVATAR_PART.EYES}>
			<g className={AVATAR_PART.PUPILS}>
				<DotEye x={LEFT_EYE_X} ink={ink} />
				<DotEye x={RIGHT_EYE_X} ink={ink} />
			</g>
		</g>
	),
	[AssistantAvatarEyes.Happy]: ({ ink }) => (
		<g>
			<ArcEye x={LEFT_EYE_X} ink={ink} />
			<ArcEye x={RIGHT_EYE_X} ink={ink} />
		</g>
	),
	[AssistantAvatarEyes.Wink]: ({ ink }) => (
		<g>
			<g className={AVATAR_PART.PUPILS}>
				<DotEye x={LEFT_EYE_X} ink={ink} />
			</g>
			<ArcEye x={RIGHT_EYE_X} ink={ink} />
		</g>
	),
	[AssistantAvatarEyes.Stars]: ({ ink }) => (
		<g className={AVATAR_PART.EYES}>
			<g className={AVATAR_PART.PUPILS}>
				<path d={starPath(LEFT_EYE_X, 60, 7.5)} fill={ink} />
				<path d={starPath(RIGHT_EYE_X, 60, 7.5)} fill={ink} />
			</g>
		</g>
	),
};

interface AvatarEyesProps {
	type: AssistantAvatarEyes;
	ink: string;
}

export const AvatarEyes: FC<AvatarEyesProps> = ({ type, ink }) => {
	const Eyes = EYES[type];
	return <Eyes ink={ink} />;
};
